import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography, Box, Card, CardContent, Divider, Alert, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { updateStatus } from "../../../../actions/fetchDataAction";
import { getVehicleInfo } from "../../../../actions/formAction";
import Progress from "../../../../components/Loader/Progress ";
import { t } from "i18next";

const ReactivateVehicleModal = ({ isOpen, onClose, vehicleId }) => {
  const dispatch = useDispatch();
  const vehicleInfo = useSelector((state) => state.vehicleInfo);
  const [vehicleData, setVehicleData] = useState(null);

  useEffect(() => {
    if (isOpen && vehicleId) {
      dispatch(getVehicleInfo({ vehicleId }));
    }
  }, [dispatch, isOpen, vehicleId]);

  useEffect(() => {
    if (vehicleInfo.loading === "succeeded" && vehicleInfo.Object) {
      setVehicleData(vehicleInfo.Object);
    }
  }, [vehicleInfo]);

  const handleStatusChange = () => {
    dispatch(
      updateStatus({
        id: vehicleId,
        status: 2, // Change status to 2 (Arrived)
      })
    );
    onClose();
  };

  const getVehicleDisplayInfo = () => {
    if (!vehicleData) return null;
    const make = vehicleData?.make?.name || "";
    const model = vehicleData?.model?.name || "";
    const description = `${vehicleData?.vehicleType?.name || ""} ${vehicleData?.year ?? ""} ${make} ${model} ${vehicleData?.color?.name || ""}`.trim();
    
    return {
      description: description || '-',
      vin: vehicleData?.vin || '-',
      client: vehicleData?.client?.name || '-',
      vehicleType: vehicleData?.vehicleType?.name || '-',
      fuelType: vehicleData?.fuelType?.name || '-',
      entryDate: vehicleData?.entryDate || '-',
    };
  };

  const displayInfo = getVehicleDisplayInfo();

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #0B3C5D 0%, #1a5f8f 100%)',
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '1.3rem',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <RestartAltIcon sx={{ fontSize: '1.5rem' }} />
        {t("Reactivate Vehicle")}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {vehicleInfo.loading === "pending" ||
        vehicleInfo.loading === "idle" ? (
          <Progress Msg={t("Loading vehicle information...")} />
        ) : vehicleInfo.loading === "failed" ? (
          <Alert severity="error">
            {t("Error loading vehicle information. Please try again.")}
          </Alert>
        ) : (
          <>
            {/* Warning Alert */}
            <Alert severity="warning" sx={{ mb: 2 }} icon={<InfoIcon />}>
              {t("You are about to reactivate a cancelled vehicle. This action will change its status to Arrived.")}
            </Alert>

            {/* Vehicle Information Card */}
            <Card sx={{ 
              bgcolor: '#f8f9fa',
              border: '2px solid #e0e0e0',
              borderRadius: 2,
              mb: 2
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DirectionsCarIcon sx={{ mr: 1, color: '#0B3C5D', fontSize: '1.3rem' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0B3C5D' }}>
                    {t("Vehicle Details")}
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />

                {displayInfo ? (
                  <>
                    {/* Vehicle Description */}
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: '#fff',
                      borderRadius: 1,
                      border: '1px solid #d0d0d0',
                      mb: 2
                    }}>
                      <Typography variant="body2" sx={{ color: '#999', mb: 0.5, fontSize: '0.85rem' }}>
                        {t("Description")}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        fontWeight: 'bold', 
                        color: '#0B3C5D',
                        fontSize: '1.1rem',
                        wordBreak: 'break-word'
                      }}>
                        {displayInfo.description}
                      </Typography>
                    </Box>

                    {/* Vehicle Details Grid */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                            {t("VIN")}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0B3C5D', wordBreak: 'break-word' }}>
                            {displayInfo.vin}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                            {t("Type")}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0B3C5D' }}>
                            {displayInfo.vehicleType}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                            {t("Fuel Type")}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0B3C5D' }}>
                            {displayInfo.fuelType}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.2, bgcolor: '#f0f4f8', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.3 }}>
                            {t("Client")}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#0B3C5D' }}>
                            {displayInfo.client}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Status Change Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#999', mb: 0.5, fontSize: '0.85rem' }}>
                          {t("Current Status")}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: '#d32f2f',
                          fontSize: '0.95rem'
                        }}>
                          Cancelled
                        </Typography>
                      </Box>

                      <Divider orientation="vertical" flexItem />

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#999', mb: 0.5, fontSize: '0.85rem' }}>
                          {t("New Status")}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.2rem' }} />
                          <Typography variant="body1" sx={{ 
                            fontWeight: 'bold', 
                            color: '#4caf50',
                            fontSize: '0.95rem'
                          }}>
                            Arrived
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {t("No vehicle data available")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            textTransform: 'capitalize',
            fontWeight: 'bold'
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleStatusChange}
          variant="contained"
          color="warning"
          startIcon={<RestartAltIcon />}
          disabled={vehicleInfo.loading === "pending" || vehicleInfo.loading === "idle"}
          sx={{ 
            textTransform: 'capitalize',
            fontWeight: 'bold'
          }}
        >
          {t("Reactivate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReactivateVehicleModal;
